import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  userSettingsApi,
  useCreateUserSettingsPrpFormatMutation,
  useCreateUserSettingsPrpUdfMutation,
  useDeleteUserSettingsPrpFormatMutation,
  useDeleteUserSettingsPrpUdfMutation,
  useDragUserSettingsPrpFormatMutation,
  useGetUserSettingsCmsTermsQuery,
  useGetUserSettingsPrpFormatQuery,
  useGetUserSettingsPrpPredefinedFieldsQuery,
  useGetUserSettingsPrpUserDefinedFieldsQuery,
  usePatchUserSettingsPrpFormatMutation,
  usePatchUserSettingsPrpUdfMutation,
  type IUserSettingsPrpFormatRow,
  type IUserSettingsPrpOrderEntry,
  type IUserSettingsPrpPredefinedField,
  type IUserSettingsPrpUdf,
  type IUserSettingsPrpUdfPatch,
} from 'app/services/userSettingsApi';
import { useLibraryDrag } from '../../useLibraryDrag';
import { DeleteFormatFieldPopup, DeleteUdfPopup } from '../submittal/SubmittalPopups';
import { jqHeight } from '../submittal/submittalModel';
import ProposalFieldDetail from './ProposalFieldDetail';
import ProposalFieldLibrary, {
  proposalItemKey,
  type ProposalLibraryItemKey,
} from './ProposalFieldLibrary';
import ProposalFormatRail, { type ProposalFormatRailVariant } from './ProposalFormatRail';
import {
  deriveProposalCmsDocuments,
  PROPOSAL_FIELD_MODELS,
  type ProposalDragPayload,
  type ProposalLibraryType,
} from './proposalModel';

/**
 * The Proposal tab — `proposal/view/proposal.js` (729 LOC) +
 * `proposal/tmpl/proposal.tmpl` (416). Wave U-e: the WHOLE feature — read-only
 * rail + library + detail, the UDF lifecycle, and the format rail's three
 * writes — against the `prp/…` endpoint family.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ★★ TRANSCRIBED FROM `proposal.js`, NOT SHARED WITH `SubmittalTab.tsx`. ★★
 *
 * Discovery §1 calls Submittal and Proposal "structurally the same feature built
 * twice", and the temptation to fold them into one parameterised component is
 * the single biggest risk in this wave. Do not. The two views diverge in small,
 * load-bearing ways, and EVERY divergence would be quietly "fixed" by unifying:
 *
 *  1. ★★ PROPOSAL RE-RENDERS CONSTANTLY; SUBMITTAL ALMOST NEVER DOES.
 *     `proposal.js:96-101` wires BOTH collections straight into `render`:
 *         listenTo(collectionUserDefinedFields, 'change', self.render)
 *         listenTo(collectionPrpFormat,         'change', self.render)
 *     with **no guard**. Submittal's UDF listener carries
 *     `if (self.name_change || data.changed.name) return` (`submittal.js:119`)
 *     and its format listener is COMMENTED OUT (`:125`). So on this tab a UDF
 *     save, a UDF delete that shifts `order_id`s, and the `set()` inside
 *     `createNewField` each rebuild the entire pane from the collections —
 *     which is why `saveUserDefinedFieldData` ends with a 100 ms timer that
 *     re-applies `active_last_change` (`:290-293`): the render wiped it.
 *     `SubmittalTab`'s whole "collections vs snapshot" split exists to keep the
 *     DOM STALE. Here the split exists to keep it FRESH. Same two layers,
 *     opposite defaults.
 *  2. ★★ NO_Q1 — the detail panes are fully rendered by the template. There is
 *     no `loadPdf`, no `mouseover` binding on the library anchors, no lazy fill.
 *     Do NOT copy `filledIds`.
 *  3. ★★ NO PDF — no `pdf_file`, no upload form, no iframe, no `validatePDF`,
 *     no `viewPDF` fade, no Q15, and (because the checkbox lives in the same
 *     block) no "start from new page" and therefore no Q5.
 *  4. ★ NO TYPE POPUP — `#add_user_defined_field` creates immediately with a
 *     bare `POST {}`; submittal opens `select.udf.type.tmpl` and posts
 *     `{udf_type_id}`.
 *  5. ★ `changeUserDefinedField` IS LIVE HERE. Wave 2 deliberately did not port
 *     it because `submittal.js` sets `self.uda_<name>_changed` and never reads
 *     it. `proposal.js:250` reads it, as an early return in
 *     `updateUserDefinedField` — so on this tab a blur with no preceding `input`
 *     event contributes NOTHING to `self.data`, and therefore no PATCH.
 *  6. ★ different classes, ids and endpoints throughout — see the banners in
 *     `ProposalFormatRail.tsx` and `ProposalFieldLibrary.tsx`.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ★★ THE `#proposal` SELECTOR TRAP — WHY FOUR HANDLERS ARE NO-OPS HERE.
 *
 * `proposal.tmpl:1`'s root is `<div id="submittal">` — a copy-paste leftover
 * (Q3). So there is NO element with `id="proposal"` inside this pane; the only
 * one on the page is the nav anchor in `main.tmpl:23`, which has no children.
 * Every `$('#proposal …')` in `proposal.js` therefore matches NOTHING:
 *   · `app_tab` / `user_tab` / `cms_tab` (`:406-436`) — meant to strip `.active`
 *     from the other two library `<ul>`s so the three behave as one exclusive
 *     group. Dead. Exclusivity holds per-`<ul>` (Bootstrap's own `activate()`)
 *     but NOT across them, so a CMS row and a User row can both be highlighted.
 *   · `changeActiveDefinedsField`'s two `removeClass('active in')` calls
 *     (`:384-385`) — so switching chips does NOT clear the active row or pane
 *     here, where on submittal it does.
 *   · `activateTab`'s `li_active.removeClass('active')` and
 *     `tab_content.removeClass('active in')` (`:364-373`) — dead; only its
 *     `parentNode`-chain half survives (★ Q23).
 *   · `edit_fields` (`:329-359`) — dead anyway, `[name=edit_fields]` is in no
 *     template.
 * Submittal escapes this because `$('#submittal …')` DOES match: three elements
 * share that id and `querySelectorAll` is happy to descend into all of them.
 * Reproduced by simply not clearing the state those lines would have cleared.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * SEAMS — what is deliberately NOT here; both LANDED in wave U-f, on the SHELL:
 *   · the ↑/↓ `keydown.filterNavigation` handler (`view/main.js:51-62,100-174`)
 *     and ★ Q2 (it throws when the hash is neither `#submittal` nor `#proposal`)
 *     → `useFilterNavigation.ts`, mounted by `UserSettingsPage`. On THIS tab the
 *     walk is keyed by `proposal_submittal_selectors['#proposal']` and skips the
 *     `.empty-row` sentinel `ProposalFormatRail` renders for the `render`
 *     variant only. It drives this file through a click it SYNTHESISES 300 ms
 *     after the keypress, which is why `ProposalFormatRail` now tests
 *     `e.detail !== 0` before Q22's plain-click `/prp/format/drag/` PATCH.
 *   · Esc/Enter via `usePopupKeys` — ★ Q17's THREE duplicate `body.keyup`
 *     listeners (`main.js:41`, `submittal.js:152`, `proposal.js:133`), Esc
 *     removing every `.popup-wrapper.active` and Enter being inert (measured: 0
 *     matching `button[autofocus="true"]` on the whole page, so all three
 *     duplicates are inert and one React mount reproduces the outcome — see
 *     `UserSettingsPage`). Wave 3's verifier flagged that Esc closed NOTHING on
 *     React; it now closes both of ★ Q4's stacked `.delete-field` popups through
 *     the `button[name="cancel_delete"]` they already expose.
 */

interface ProposalTabProps {
  /** Whether `#proposal_format` is the visible pane. Only `showTooltip` cares:
   * `Dispatcher 'open:proposal'` re-runs it (`:110`), and overflow cannot be
   * measured while the pane is `display:none`. */
  active: boolean;
}

/** The data `render()` hands the template. `gen` is the render counter — see
 * `ProposalFieldDetail`'s `gen` prop for why it must reach the panes. */
interface ProposalSnapshot {
  gen: number;
  userFields: IUserSettingsPrpUdf[];
}

const EMPTY_APP_FIELDS: IUserSettingsPrpPredefinedField[] = [];

/**
 * The `ProposalLibraryItemKey` for one user field.
 *
 * ★ TWO KEYS, AND THEY ARE NOT INTERCHANGEABLE: the `<li>`'s `data-id` and the
 * anchor's `data-id` are the field's `id`, while the PANE the anchor activates
 * is `proposal_user_defined_field_<field_order>`. `render()`'s re-selection, the
 * create flow's 10 ms lookup and `active_last_change` each use a different one of
 * the two, so both are carried.
 */
const userItem = (field: IUserSettingsPrpUdf): ProposalLibraryItemKey => ({
  key: proposalItemKey('user', field.id),
  liId: String(field.id),
  anchorId: field.id,
  paneId: `proposal_user_defined_field_${field.field_order}`,
});

const ProposalTab = ({ active }: ProposalTabProps) => {
  const dispatch = useAppDispatch();

  // ── the FOUR boot fetches (`proposal.js:112-117`, one `Promise.all`) ──────
  // Four, not five: there is no `user_defined_field_options` call, because there
  // is no type popup on this tab.
  const { data: appFieldsData } = useGetUserSettingsPrpPredefinedFieldsQuery();
  const { data: udfData } = useGetUserSettingsPrpUserDefinedFieldsQuery();
  const { data: formatData } = useGetUserSettingsPrpFormatQuery();
  const { data: terms } = useGetUserSettingsCmsTermsQuery();

  const appFields = appFieldsData ?? EMPTY_APP_FIELDS;
  const cmsDocuments = useMemo(() => deriveProposalCmsDocuments(terms), [terms]);

  const [createUdf] = useCreateUserSettingsPrpUdfMutation();
  const [patchUdf] = usePatchUserSettingsPrpUdfMutation();
  const [deleteUdf] = useDeleteUserSettingsPrpUdfMutation();
  const [createFormat] = useCreateUserSettingsPrpFormatMutation();
  const [dragFormat] = useDragUserSettingsPrpFormatMutation();
  const [patchFormat] = usePatchUserSettingsPrpFormatMutation();
  const [deleteFormat] = useDeleteUserSettingsPrpFormatMutation();

  // ── layer 1: the collections ─────────────────────────────────────────────
  const fieldsRef = useRef<IUserSettingsPrpUdf[]>([]);
  const formatRef = useRef<IUserSettingsPrpFormatRow[]>([]);

  // ── layer 2: what the template was last handed ───────────────────────────
  const [snap, setSnap] = useState<ProposalSnapshot | null>(null);
  const [rail, setRail] = useState<{
    rows: IUserSettingsPrpFormatRow[];
    variant: ProposalFormatRailVariant;
  }>({ rows: [], variant: 'render' });
  const [counts, setCounts] = useState({ user: 0, cms: 0, application: 0 });

  // ── view state that SURVIVES a render (`self.*` on the Backbone view) ────
  const [activeType, setActiveType] = useState<ProposalLibraryType | undefined>('user');
  const activeTypeRef = useRef<ProposalLibraryType | undefined>('user');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const activeRowIdRef = useRef<string | null>(null);

  // ── view state that a render RESETS (the template rebuilds it) ───────────
  const [searchValue, setSearchValue] = useState('');
  const [searchHidden, setSearchHidden] = useState(false);
  /**
   * ★★ THE EXCLUSIVE HALF OF `.active` ON A LIBRARY `<li>` — **ONE SLOT PER
   * `<ul>`, NOT ONE FOR THE WHOLE LIBRARY.**
   *
   * Bootstrap's `Tab.show` does `this.activate($this.closest('li'), $ul)`, and
   * `activate` only clears `container.find('> .active')` — i.e. the `<ul>` the
   * clicked anchor belongs to. The three `app_tab`/`user_tab`/`cms_tab` handlers
   * that WOULD have stripped `.active` off the other two lists are rooted at
   * `#proposal` and match nothing (the `#proposal` banner above), so on this tab
   * `.active` legitimately ACCUMULATES across the three lists — and it is
   * visible: `styles.css:2186,2446,2450` paint `background:#d3d1d1`.
   *
   * ⚠ VERIFIED AGAINST LEGACY, wave-4 verify: click a User row, switch to the
   * CMS chip and click a CMS row, switch to Application and click an App row →
   * legacy ends with all THREE highlighted; a single global slot ends with one.
   * Do not collapse this back into one key.
   */
  const [activeItemKeyByType, setActiveItemKeyByType] = useState<
    Record<ProposalLibraryType, string | null>
  >({ user: null, cms: null, application: null });
  const activeItemKeyByTypeRef = useRef<Record<ProposalLibraryType, string | null>>({
    user: null,
    cms: null,
    application: null,
  });
  /** The ADDITIVE half of `.active` on a library `<li>` — `active_last_change`
   * (`:295-300`) and `activateTab` (`:371`) both use `addClass`/`classList.add`
   * with no matching remove, and the `*_tab` handlers that would clean up after
   * them are dead here (see the `#proposal` banner). Cleared ONLY for the `<ul>`
   * a Bootstrap tab show touches, for the same reason as above. */
  const [extraActiveKeys, setExtraActiveKeys] = useState<ReadonlySet<string>>(() => new Set());
  const extraActiveKeysRef = useRef<ReadonlySet<string>>(new Set());
  /** The pane Bootstrap's `activate($target, $target.parent())` last made
   * `.active`. Exclusive — every pane lives in the SAME `#myTabContent`, so
   * unlike the `<li>`s there is one slot here. */
  const [activePaneId, setActivePaneId] = useState<string | null>(null);
  const activePaneIdRef = useRef<string | null>(null);
  /**
   * ★★ THE ADDITIVE HALF OF `.active` ON A PANE. `activateTab` (`:374-376`) and
   * `active_last_change` (`:299`) both `addClass("active in")` and the
   * `$("#proposal #myTabContent .active").removeClass(...)` that would undo it
   * is dead. So pressing a library trash BUTTON leaves the pane it activates
   * `.active` ALONGSIDE whichever pane Bootstrap had already activated — two
   * documents stacked in the detail column.
   *
   * ⚠ VERIFIED AGAINST LEGACY, wave-4 verify: with pane
   * `proposal_user_defined_field_6` showing, pressing field 13's trash button
   * leaves BOTH `_1` and `_6` `.active in`. A single slot shows only `_1`.
   * Cleared by the next Bootstrap tab show, which is the only thing that clears
   * it on legacy either.
   */
  const [extraActivePaneIds, setExtraActivePaneIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const extraActivePaneIdsRef = useRef<ReadonlySet<string>>(new Set());
  const [panesWithIn, setPanesWithIn] = useState<ReadonlySet<string>>(() => new Set());
  const [tooltipItemKeys, setTooltipItemKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [tooltipRowIds, setTooltipRowIds] = useState<ReadonlySet<number>>(() => new Set());
  const [invalidCmsTitles, setInvalidCmsTitles] = useState<ReadonlySet<string>>(() => new Set());

  // ── popups ───────────────────────────────────────────────────────────────
  const [deletePopupField, setDeletePopupField] = useState<IUserSettingsPrpUdf | null>(null);
  /**
   * ★★ Q4 — AN ARRAY, NOT A NULLABLE SINGLE POPUP. One click on a rail trash
   * button opens TWO identical `.delete-field` popups (the `<i>` and the
   * `<button>` both match `'click .delete'` and `render_delete_popup_proposal`
   * never stops the bubble). Confirming or cancelling clears the whole array,
   * because `remove_confirm_popup` removes `#popup-content.delete-field` as a
   * SET — and exactly one DELETE goes out.
   */
  const [deleteFormatPopups, setDeleteFormatPopups] = useState<IUserSettingsPrpFormatRow[]>([]);

  // ── the remaining `self.*` scalars, verbatim ─────────────────────────────
  /** `self.data` — the accumulating PATCH body. Shared across fields, as legacy
   * shares it, though the `uda_*_changed` gate below makes leaks far rarer than
   * on submittal. */
  const dataRef = useRef<IUserSettingsPrpUdfPatch>({});
  /**
   * ★ `self.uda_<name>_changed` — `changeUserDefinedField` (`:239-244`) sets it
   * on `input`, `updateUserDefinedField` (`:245-259`) EARLY-RETURNS unless it is
   * set and then clears it. Submittal has the identical pair of handlers and
   * simply never reads the flag (§5.2 #6 "LIVE but pointless"), which is why
   * wave 2 left it out. Here it is the difference between a blur that PATCHes
   * and a blur that does nothing.
   */
  const udaChangedRef = useRef<{ name?: boolean; content?: boolean }>({});
  /** `self.field_id`, initialised to `1` at `proposal.js:89`. */
  const fieldIdRef = useRef<number>(1);
  /** `self.popup_for` — set by `render_popup`, read by `removeUserDefinedField`
   * AND as `saveUserDefinedFieldData`'s id fallback (`:274`). */
  const popupForRef = useRef<number | null>(null);
  /** `self.open_for` — set by `activateTab` (★ Q23). */
  const openForRef = useRef<number | null>(null);
  /** `self.delete_popup_for` — the format row's `data-id`, as the STRING it came
   * off the attribute as (`:460`), read by `removePRPStructureField` (`:507`). */
  const deletePopupForRef = useRef<string | null>(null);
  /** `self.active_row_clicked` — makes the NEXT `clickToItem` scroll. */
  const activeRowClickedRef = useRef(false);
  /** `self.last_clicked` — the `<li>`'s `data-id`, re-clicked at the END of
   * every `render()` (`:182`). */
  const lastClickedRef = useRef<string | null>(null);
  /** `self.selected_format` — the active row's `data-target`, also re-clicked by
   * `render()` (`:181`). */
  const selectedFormatRef = useRef<number | null>(null);
  /** `self.last_action` — set to `'create_new_field'` by `createNewField`
   * (`:571`) purely to suppress the re-fetch in `fetch_proposal_format`. */
  const lastActionRef = useRef<string>('');
  /** Legacy's `self.dropping` (`:647,651`) with the dropped item's four
   * attributes attached. Non-null exactly while a LIBRARY drag is in flight. */
  const dragPayloadRef = useRef<ProposalDragPayload | null>(null);

  const railWrapperRef = useRef<HTMLDivElement | null>(null);
  /** `#proposal-format-list-wrapper-row` — `connectToSortable`'s target, and
   * therefore the only box a library drag may be released over. */
  const railRef = useRef<HTMLDivElement | null>(null);
  /** `.proposal-user-udf-scroll` — `render()` captures its `scrollTop` on entry
   * (`:147`) and restores it on exit (`:180`). Submittal has no equivalent. */
  const userScrollRef = useRef<HTMLDivElement | null>(null);
  const labelRefs = useRef(new Map<number, HTMLSpanElement>());
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  const anchorRefs = useRef(new Map<string, HTMLAnchorElement>());
  const renderSelectsTimer = useRef<number | undefined>(undefined);
  const activeChangeTimer = useRef<number | undefined>(undefined);

  const setActiveRow = useCallback((value: string | null) => {
    activeRowIdRef.current = value;
    setActiveRowId(value);
  }, []);
  /** Bootstrap's `activate(li, $ul)` — replaces the exclusive `.active` in ONE
   * `<ul>` and leaves the other two lists exactly as they were. `null` for the
   * type clears just that list (what `render()` does, for all three). */
  const setActiveItemFor = useCallback(
    (type: ProposalLibraryType | 'all', value: string | null) => {
      const next: Record<ProposalLibraryType, string | null> =
        type === 'all'
          ? { user: null, cms: null, application: null }
          : { ...activeItemKeyByTypeRef.current, [type]: value };
      activeItemKeyByTypeRef.current = next;
      setActiveItemKeyByType(next);
    },
    []
  );
  const setExtraActive = useCallback((value: ReadonlySet<string>) => {
    extraActiveKeysRef.current = value;
    setExtraActiveKeys(value);
  }, []);
  const setActivePane = useCallback((value: string | null) => {
    activePaneIdRef.current = value;
    setActivePaneId(value);
  }, []);
  const setExtraActivePanes = useCallback((value: ReadonlySet<string>) => {
    extraActivePaneIdsRef.current = value;
    setExtraActivePaneIds(value);
  }, []);

  /** RTK Query cache mirror — zero network; it only keeps the cache from
   * resurrecting a deleted row if the SPA remounts this route. See the "STILL NO
   * TAG TYPES" banner in `userSettingsApi.ts`. */
  const patchUdfCache = useCallback(
    (recipe: (draft: IUserSettingsPrpUdf[]) => void) => {
      dispatch(
        userSettingsApi.util.updateQueryData('getUserSettingsPrpUserDefinedFields', undefined, recipe)
      );
    },
    [dispatch]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // READ-SIDE HANDLERS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * `clickToItem` (`:693-711`). Runs on EVERY library-row click, including the
   * ones `rowClick`, `removeUserDefinedField` and `render()` synthesise.
   *
   * ★ The clicked row's own type is deliberately NOT a factor: legacy compares
   * the ACTIVE FORMAT ROW's type against `self.activeDefinedsField`, not against
   * the clicked element.
   */
  const clickToItem = useCallback(
    (item: ProposalLibraryItemKey) => {
      lastClickedRef.current = item.liId;
      selectedFormatRef.current = null;
      const activeRow = formatRef.current.find((r) => `row-${r.id}` === activeRowIdRef.current);
      if (activeRow) {
        const rowType = PROPOSAL_FIELD_MODELS[activeRow.model ?? ''];
        const target = activeRow.object_id == null ? '' : String(activeRow.object_id);
        const cms = activeRow.cms_field_name ?? '';
        const clickedId = item.anchorId == null ? '' : String(item.anchorId);
        if (rowType !== activeTypeRef.current || (target !== clickedId && !(cms && cms === item.cms))) {
          setActiveRow(null);
        }
      }
      if (activeRowClickedRef.current) {
        itemRefs.current.get(item.key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      activeRowClickedRef.current = false;
    },
    [setActiveRow]
  );

  /**
   * One library row click = Bootstrap's `tab('show')` (exclusive `.active` on
   * the `<li>` WITHIN ITS OWN `<ul>`, exclusive `.active` + sticky `.in` on the
   * pane) PLUS `clickToItem`.
   *
   * ★★ THE THREE `app_tab`/`user_tab`/`cms_tab` HANDLERS THAT WOULD ALSO STRIP
   * `.active` FROM THE OTHER TWO `<ul>`s ARE DEAD ON THIS TAB — see the
   * `#proposal` banner. So everything below is scoped to the clicked row's OWN
   * list: its exclusive slot, and the additive keys that belong to the same
   * `<ul>` (which `container.find('> .active').removeClass('active')` does
   * sweep up). Rows in the other two lists keep their highlight.
   *
   * The PANE half is the other way round: every pane is a child of the SAME
   * `#myTabContent`, so `activate($target, $target.parent())` really does clear
   * them all — including whatever `activateTab` / `active_last_change` had added
   * additively.
   */
  const selectLibraryItem = useCallback(
    (item: ProposalLibraryItemKey) => {
      const type = item.key.slice(0, item.key.indexOf(':')) as ProposalLibraryType;
      setActiveItemFor(type, item.key);
      if (extraActiveKeysRef.current.size) {
        const kept = new Set([...extraActiveKeysRef.current].filter((k) => !k.startsWith(`${type}:`)));
        if (kept.size !== extraActiveKeysRef.current.size) setExtraActive(kept);
      }
      if (extraActivePaneIdsRef.current.size) setExtraActivePanes(new Set());
      setActivePane(item.paneId);
      setPanesWithIn((prev) => (prev.has(item.paneId) ? prev : new Set(prev).add(item.paneId)));
      clickToItem(item);
    },
    [clickToItem, setActiveItemFor, setActivePane, setExtraActive, setExtraActivePanes]
  );

  /**
   * `render()` (`proposal.js:145-183`). Rebuilds the whole pane from the
   * collections and therefore annihilates every imperative patch — the search
   * box empties and un-hides, the active library row and pane are cleared, the
   * hand-injected rail row disappears. `self.active_row_id` is the one thing
   * restored (`:163-164`), and only if that row still exists.
   *
   * ★ AND THEN IT RE-SELECTS. The last two lines of `render()` are
   *     $(`.proposal-row a[data-id="${self.selected_format}"]`).click();
   *     if (self.last_clicked) $el.find(`li[data-id="${self.last_clicked}"] a`).click()
   * which submittal's `render()` does NOT have (submittal does it inside
   * `render_selects` instead). So on this tab every render restores the library
   * selection by itself.
   */
  const doRender = useCallback(
    (fields: IUserSettingsPrpUdf[], format: IUserSettingsPrpFormatRow[]) => {
      // `:147` — captured on entry, restored at `:180`.
      const top = userScrollRef.current?.scrollTop ?? 0;

      fieldsRef.current = fields;
      formatRef.current = format;
      setSnap((prev) => ({ gen: (prev?.gen ?? 0) + 1, userFields: fields }));
      setRail({ rows: format, variant: 'render' });
      setCounts({ user: fields.length, application: appFields.length, cms: cmsDocuments.length });
      setPanesWithIn(new Set());
      setActiveItemFor('all', null);
      setActivePane(null);
      setExtraActivePanes(new Set());
      setSearchValue('');
      setSearchHidden(false);
      setTooltipItemKeys(new Set());
      setTooltipRowIds(new Set());
      setExtraActive(new Set());
      const keep =
        activeRowIdRef.current && format.some((r) => `row-${r.id}` === activeRowIdRef.current)
          ? activeRowIdRef.current
          : null;
      setActiveRow(keep);

      // `:181` — `$('.proposal-row a[data-id="<selected_format>"]').click()`.
      // The selector spans all three `<ul>`s; the User list comes first in the
      // DOM, so a collision resolves to the user anchor. CMS anchors carry no
      // `data-id` and can never match.
      const selected = selectedFormatRef.current;
      if (selected != null) {
        const field = fields.find((f) => f.id === selected);
        if (field) selectLibraryItem(userItem(field));
        else {
          const app = appFields.find((f) => f.id === selected);
          if (app) {
            selectLibraryItem({
              key: proposalItemKey('application', app.value),
              liId: `app-${app.value}`,
              anchorId: app.id,
              paneId: app.value,
            });
          }
        }
      }
      // `:182` — `$el.find('li[data-id="<last_clicked>"] a').click()`.
      const last = lastClickedRef.current;
      if (last) {
        const field = fields.find((f) => String(f.id) === last);
        if (field) selectLibraryItem(userItem(field));
        else {
          const doc = cmsDocuments.find((d) => d.value === last);
          if (doc) {
            selectLibraryItem({
              key: proposalItemKey('cms', doc.value),
              liId: doc.value,
              cms: doc.value,
              paneId: doc.key,
            });
          } else {
            const app = appFields.find((f) => `app-${f.value}` === last);
            if (app) {
              selectLibraryItem({
                key: proposalItemKey('application', app.value),
                liId: `app-${app.value}`,
                anchorId: app.id,
                paneId: app.value,
              });
            }
          }
        }
      }

      if (userScrollRef.current) userScrollRef.current.scrollTop = top;
    },
    [
      appFields,
      cmsDocuments,
      selectLibraryItem,
      setActiveItemFor,
      setActivePane,
      setActiveRow,
      setExtraActive,
      setExtraActivePanes,
    ]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // BOOT — `initialize`'s `Promise.all(...).then(...)` (`:112-130`)
  // ═══════════════════════════════════════════════════════════════════════
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    if (!appFieldsData || !udfData || !formatData || !terms) return;
    booted.current = true;
    doRender(udfData, formatData);
    /**
     * `:129` — `…li.each-element-row:nth-child(1) a.item-for-drag').click()`.
     * ★ NO_Q1: unlike submittal, this click is enough. The pane it activates is
     * already fully rendered by the template, so the right-hand column is
     * populated on first paint; submittal's is blank until the pointer crosses a
     * library row.
     */
    const first = udfData[0];
    if (!first) return;
    const item = userItem(first);
    setActiveItemFor('user', item.key);
    setActivePane(item.paneId);
    setPanesWithIn(new Set([item.paneId]));
    lastClickedRef.current = String(first.id);
    selectedFormatRef.current = null;
  }, [appFieldsData, udfData, formatData, terms, doRender, setActiveItemFor, setActivePane]);

  // ═══════════════════════════════════════════════════════════════════════
  // showTooltip (`:657-671`) — measured after every render and on tab open
  // ═══════════════════════════════════════════════════════════════════════
  useLayoutEffect(() => {
    if (!snap) return;
    // ★ Only `.user-defiened-fields` (the USER anchors) and the rail's
    // `.prօposal_format_field` spans are measured; CMS and Application rows
    // never get a tooltip.
    const nextItems = new Set<string>();
    anchorRefs.current.forEach((el, key) => {
      if (key.startsWith('user:') && el.scrollWidth > el.offsetWidth) nextItems.add(key);
    });
    const nextRows = new Set<number>();
    labelRefs.current.forEach((el, id) => {
      if (el.scrollWidth > el.offsetWidth) nextRows.add(id);
    });
    // Legacy only ever ADDS the class, and nothing removes it short of a full
    // template rebuild — so union, and let `doRender` reset.
    setTooltipItemKeys((prev) => {
      const merged = new Set(prev);
      nextItems.forEach((k) => merged.add(k));
      return merged.size === prev.size ? prev : merged;
    });
    setTooltipRowIds((prev) => {
      const merged = new Set(prev);
      nextRows.forEach((k) => merged.add(k));
      return merged.size === prev.size ? prev : merged;
    });
  }, [snap, rail, active]);

  useEffect(
    () => () => {
      if (renderSelectsTimer.current !== undefined) window.clearTimeout(renderSelectsTimer.current);
      if (activeChangeTimer.current !== undefined) window.clearTimeout(activeChangeTimer.current);
    },
    []
  );

  /**
   * `changeActiveDefinedsField` (`:378-405`).
   *
   * ★★ IT DOES NOT CLEAR THE ACTIVE ROW OR PANE ON THIS TAB. The two lines that
   * would (`:384-385`) are rooted at `#proposal` and match nothing — see the
   * `#proposal` banner. Submittal's equivalent DOES clear both. Not "fixed".
   */
  const changeActiveDefinedsField = useCallback((type: ProposalLibraryType | undefined) => {
    if (activeTypeRef.current === type) return; // legacy's early return (`:380`)
    activeTypeRef.current = type;
    setActiveType(type);
    // ★ `.hide` on BOTH the wrapper and the input — they share the class. A full
    // `render()` re-emits the box unhidden, which is why `doRender` resets this.
    setSearchHidden(type !== 'user');
  }, []);

  /** `rowClick` (`:672-692`) — the cross-highlight from rail to library.
   * ★ `self.active_row_id = $(e.currentTarget).attr('id')` (`:677`) reads the id
   * back OUT of the DOM, so for the hand-injected row it is the LIBRARY item's
   * id (★ Q12), not the format row's. Hence `domId`. */
  const onRowClick = useCallback(
    (row: IUserSettingsPrpFormatRow, domId: string) => {
      setActiveRow(`row-${domId}`);
      selectedFormatRef.current = row.object_id;
      activeRowClickedRef.current = true;
      const type = PROPOSAL_FIELD_MODELS[row.model ?? ''];
      if (!type) return; // a placeholder row (`model: null`) highlights and stops
      // `$('.proposal-block-fields-wrap[data-type=…]').click()` — the chip
      // handler is bound to `.not-active`, so a no-op when already active. The
      // explicit `.show-row` swap on the next two lines is then redundant.
      changeActiveDefinedsField(type);
      if (row.cms_field_name) {
        const doc = cmsDocuments.find((d) => d.value === row.cms_field_name);
        if (doc) {
          selectLibraryItem({
            key: proposalItemKey('cms', doc.value),
            liId: doc.value,
            cms: doc.value,
            paneId: doc.key,
          });
        }
        return;
      }
      if (type === 'user') {
        const field = (snap?.userFields ?? []).find((f) => f.id === row.object_id);
        if (field) selectLibraryItem(userItem(field));
        return;
      }
      if (type === 'application') {
        const app = appFields.find((f) => f.id === row.object_id);
        if (app) {
          selectLibraryItem({
            key: proposalItemKey('application', app.value),
            liId: `app-${app.value}`,
            anchorId: app.id,
            paneId: app.value,
          });
        }
      }
    },
    [appFields, changeActiveDefinedsField, cmsDocuments, selectLibraryItem, setActiveRow, snap]
  );

  /** `check_scroll` (`:552-559`) — ★ Q24's hard-coded 40 px row height. */
  const onWrapperScroll = useCallback((wrapper: HTMLDivElement) => {
    wrapper.scrollLeft = 0;
    const max = (formatRef.current.length + 3) * 40 - jqHeight(wrapper);
    if (wrapper.scrollTop > max) wrapper.scrollTop = max;
  }, []);

  /** `checkField` (`:260-271`) as bound to the CMS panes' READONLY title inputs.
   * Real (it toggles `.invalid-field`) but inert in practice — ★ Q21. */
  const onCmsTitleCheck = useCallback((docValue: string, input: HTMLInputElement) => {
    const invalid = input.value.length < 3;
    setInvalidCmsTitles((prev) => {
      if (invalid === prev.has(docValue)) return prev;
      const next = new Set(prev);
      if (invalid) next.add(docValue);
      else next.delete(docValue);
      return next;
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // WRITE-SIDE HANDLERS — the UDF lifecycle
  // ═══════════════════════════════════════════════════════════════════════

  /** `render_selects` (`:184-204`) — a 300 ms-debounced re-render of the RAIL
   * ONLY (plus the User counter), from `proposal.format.tmpl`. Reached from
   * exactly one place: `removeUserDefinedField` (`:327`).
   * ★ Unlike submittal's, it does NOT re-click `selected_format` /
   * `last_clicked` — `render()` owns that on this tab. */
  const renderSelects = useCallback(() => {
    if (renderSelectsTimer.current !== undefined) window.clearTimeout(renderSelectsTimer.current);
    renderSelectsTimer.current = window.setTimeout(() => {
      setRail({ rows: [...formatRef.current], variant: 'renderSelects' });
      setCounts((prev) => ({ ...prev, user: fieldsRef.current.length }));
      renderSelectsTimer.current = undefined;
    }, 300);
  }, []);

  /** ★ `changeUserDefinedField` (`:239-244`) — `input .user_defined_field`. The
   * handler wave 2 skipped, because submittal never reads its flag. */
  const changeUserDefinedField = useCallback((attr: 'name' | 'content') => {
    udaChangedRef.current[attr] = true;
  }, []);

  /** `updateUserDefinedField` (`:245-259`) — accumulates into `self.data`, but
   * ONLY if `changeUserDefinedField` armed the flag first. */
  const updateUserDefinedField = useCallback(
    (attr: 'name' | 'content', value: string, fieldId: number) => {
      if (!udaChangedRef.current[attr]) return; // ★ `:250`
      udaChangedRef.current[attr] = false;
      fieldIdRef.current = fieldId;
      dataRef.current[attr] = value;
    },
    []
  );

  /**
   * `active_last_change` (`:295-300`).
   *
   * ★ NOT THE BUGGY SUBMITTAL TWIN. Submittal's version is called with the
   * field's `id` and looks the `<li>` up by `submittal_udf_item_field_<ID>`
   * while the template builds that id from `field_order` — so it usually lands
   * on nothing. Proposal's is called with `field_order` (`:284,291`) and the
   * template uses `field_order`, so it lands on the right row every time. Do not
   * copy submittal's `byWrongKey` lookup.
   *
   * Both the `<li>` and the pane get their class with `addClass`, i.e.
   * additively; nothing on this tab removes them except a render or a Bootstrap
   * tab activation.
   */
  const activeLastChange = useCallback(
    (fieldOrder: number) => {
      const field = fieldsRef.current.find((f) => f.field_order === fieldOrder);
      if (field) {
        setExtraActive(new Set([...extraActiveKeysRef.current, proposalItemKey('user', field.id)]));
      }
      const paneId = `proposal_user_defined_field_${fieldOrder}`;
      // ★ `addClass("active in")` — ADDITIVE, so it goes into the extra set and
      // leaves whatever Bootstrap had activated alone.
      setExtraActivePanes(new Set([...extraActivePaneIdsRef.current, paneId]));
      setPanesWithIn((prev) => (prev.has(paneId) ? prev : new Set(prev).add(paneId)));
    },
    [setExtraActive, setExtraActivePanes]
  );

  /**
   * `saveUserDefinedFieldData` (`:272-294`) — §6 W2/W3 against `prp/`.
   *
   * Legacy's exact sequence, and why the port collapses two renders into one:
   *   1. `model.save(data, {patch:true})` → Backbone `set`s first → collection
   *      `'change'` → **render #1**;
   *   2. `self.data = {}`; `active_last_change(field_order)`;
   *   3. `if (name)` → `collectionPrpFormat.each(m => m.set('name', name))` for
   *      every format row pointing at this field → collection `'change'` →
   *      **render #2**, which wipes the classes step 2 just applied;
   *   4. a 100 ms timer re-applies `active_last_change`.
   * The port does the collection surgery, ONE `doRender`, then
   * `activeLastChange`, then legacy's 100 ms timer. ⚠ DEVIATION, recorded: the
   * only difference is that legacy leaves the row/pane un-highlighted for those
   * 100 ms in the rename case. Same end state, one fewer full rebuild.
   *
   * ⚠ ALSO NOT REPRODUCED: step 3's `set()` fires `ModelPRPStructure.onchange`
   * (`prp.format.js:29-42`) for any format model created in THIS session, which
   * PATCHes `{"name": …}` to `…/prp/format/<id>` — a body with no `type`, i.e. a
   * guaranteed **500** via R8. Fetched models never bind `onchange` (it is
   * armed inside a `listenToOnce('sync')` and a collection fetch does not sync
   * its members), so the path needs a drop and a rename in the same page life.
   * Reproducing a request that can only 500 would be mirroring a crash, not a
   * behaviour; reported under NOTES_FOR_OWNER instead.
   */
  const saveUserDefinedFieldData = useCallback(() => {
    const id = fieldIdRef.current || popupForRef.current;
    const model = fieldsRef.current.find((f) => f.id === Number(id));
    // ⚠ Legacy does `collection.get(id).get(key)` unguarded and would throw.
    if (!model) return;
    const name = dataRef.current.name;
    const body = { ...dataRef.current };
    let hasChanges = false;
    (Object.keys(body) as (keyof IUserSettingsPrpUdfPatch)[]).forEach((key) => {
      // Legacy compares with `!=`; kept loose so the coercions match.
      // eslint-disable-next-line eqeqeq
      if (body[key] != model[key]) hasChanges = true;
    });
    if (!hasChanges) return; // ★ `self.data` is NOT cleared on this branch
    patchUdf({ id: model.id, body });
    dataRef.current = {};
    // Backbone's `save(attrs, {patch:true})` `set`s first — the collection learns
    // the new values, and (unlike submittal) that `set` is what re-renders.
    fieldsRef.current = fieldsRef.current.map((f) => (f.id === model.id ? { ...f, ...body } : f));
    patchUdfCache((draft) => {
      const row = draft.find((f) => f.id === model.id);
      if (row) Object.assign(row, body);
    });
    if (name) {
      // `:286-289` — every format row pointing at this UDF takes the new name.
      // ★ Note what is NOT here: submittal additionally patches the rendered
      // rail label and the `<li>` by hand (`submittal.js:459-465`) because it
      // does not re-render. Proposal only touches the collection.
      formatRef.current = formatRef.current.map((r) =>
        r.object_id === model.id ? { ...r, name } : r
      );
    }
    doRender(fieldsRef.current, formatRef.current);
    activeLastChange(model.field_order);
    // `:290-293` — the 100 ms re-apply.
    if (activeChangeTimer.current !== undefined) window.clearTimeout(activeChangeTimer.current);
    activeChangeTimer.current = window.setTimeout(() => {
      activeLastChange(model.field_order);
      activeChangeTimer.current = undefined;
    }, 100);
  }, [activeLastChange, doRender, patchUdf, patchUdfCache]);

  const onTitleBlur = useCallback(
    (field: IUserSettingsPrpUdf, value: string) => {
      // `blur .user_defined_field` is bound BEFORE `blur .title_edit`
      // (`proposal.js:49` vs `:53`), and jQuery fires delegated handlers in
      // binding order, so `updateUserDefinedField` always runs first.
      updateUserDefinedField('name', value, field.id);
      saveUserDefinedFieldData();
    },
    [saveUserDefinedFieldData, updateUserDefinedField]
  );

  const onContentBlur = useCallback(
    (field: IUserSettingsPrpUdf, html: string) => {
      updateUserDefinedField('content', html, field.id);
      saveUserDefinedFieldData();
    },
    [saveUserDefinedFieldData, updateUserDefinedField]
  );

  /**
   * `addUserDefinedField` (`:217-238`) — the Proposal tab's create.
   *
   * ★ NO POPUP AND AN EMPTY BODY: `new ModelUserDefinedField()` + a bare
   * `field.save()` → `POST …/prp/user_defined_fields/ {}`. Submittal opens a
   * type popup and posts `{udf_type_id}`.
   *
   * ★ Q11 — `collection.unshift(field)` puts the new row FIRST even though the
   * API sorts by `name`, so a refresh moves it alphabetically. Legacy unshifts
   * SYNCHRONOUSLY, before the response; the render only happens when the
   * response's `set` fires the collection's `change`, by which time the model
   * has its id, name and `field_order`. Awaiting the create and rendering once is
   * the same observable sequence.
   *
   * ★ Legacy does NOT capture/restore the rail's `scrollTop` here (submittal
   * does, `submittal.js:264,268`). `render()` restores the LIBRARY's scroll
   * instead.
   */
  const addUserDefinedField = useCallback(async () => {
    let created: IUserSettingsPrpUdf;
    try {
      created = await createUdf().unwrap();
    } catch {
      // Backbone's `.then` success branch simply never runs. No error UI exists.
      return;
    }
    const nextFields = [created, ...fieldsRef.current];
    doRender(nextFields, formatRef.current);
    patchUdfCache((draft) => {
      draft.unshift(created);
    });
    window.setTimeout(() => {
      // `:222-224` — the new library row is looked up by FIELD_ORDER and clicked.
      const target = fieldsRef.current.find((f) => f.field_order === created.field_order);
      if (target) selectLibraryItem(userItem(target));
      /**
       * ★★★ `:225-233` — THE FOCUS STEP IS DEAD, AND IT MUST STAY DEAD.
       *
       * Legacy looks the title input up by
       * `#proposal_user_defined_field_<field_order>  input.title_user_defined_field_<id>`
       * (both keys, one selector — and it really does match), clears and
       * restores its value to park the caret at the end, and calls `focus()`.
       * It never takes: the line above it `.click()`s the library anchor, and
       * Bootstrap's `Tab.activate` sees a `.fade` pane, so it defers `next()` —
       * the `display:block` half — behind `emulateTransitionEnd(150)`. At
       * `focus()` time the pane is still `display:none`, and focusing a hidden
       * element is a no-op.
       *
       * ⚠ MEASURED (wave-4 verify): a `focusin`/`focusout` trace over the whole
       * create on legacy contains exactly two entries — the `+` button gaining
       * focus and losing it when `render()` destroys it — and `document
       * .activeElement` is `<body>` for the following 2.5 s, while the pane and
       * the input both exist and the row IS activated. Reproducing the focus
       * would put a caret in a field legacy leaves alone.
       */
    }, 10);
  }, [createUdf, doRender, patchUdfCache, selectLibraryItem]);

  /** `render_popup` (`:437-457`) — the UDF delete confirmation. `stopPropagation`
   * lives on the elements (see `ProposalFieldLibrary`), which is why the nested
   * `<i>` opens exactly ONE popup while the `<button>` also runs ★ Q23's
   * `activateTab`. */
  const onRemoveClick = useCallback((fieldId: number) => {
    popupForRef.current = fieldId;
    const field = fieldsRef.current.find((f) => f.id === fieldId);
    // ⚠ Legacy does `collection.get(popup_for).toJSON()` unguarded.
    if (field) setDeletePopupField(field);
  }, []);

  /**
   * ★★ Q23 — `activateTab` (`:360-377`), bound to `click .btn-udf-delete`, i.e.
   * to the SAME `<button>` that `click .remove` is bound to. jQuery runs both
   * handlers for one press because `stopPropagation` does not stop siblings on
   * the same element (only `stopImmediatePropagation` would). So pressing the
   * trash button opens the delete popup AND activates that row and its pane.
   *
   * Only two of its five statements survive the `#proposal` trap:
   *   `li_dropdown_open.classList.add("active")`  → additive `.active` on the
   *        `<li>` reached by walking five `parentNode`s;
   *   `$('.proposal_user_defined_field_' + open_for).addClass('active in')` →
   *        the pane addressed by its **id-derived CLASS**, which belongs to the
   *        pane whose **DOM id is field_order-derived**. Both keys, one element.
   */
  const activateTab = useCallback(
    (fieldId: number) => {
      openForRef.current = fieldId;
      setExtraActive(new Set([...extraActiveKeysRef.current, proposalItemKey('user', fieldId)]));
      const field = fieldsRef.current.find((f) => f.id === fieldId);
      if (!field) return;
      const paneId = `proposal_user_defined_field_${field.field_order}`;
      // ★★ `addClass('active in')`, ADDITIVE — the pane Bootstrap had already
      // activated KEEPS its `.active`, so two documents end up stacked in the
      // detail column. Verified against legacy in wave-4 verify.
      setExtraActivePanes(new Set([...extraActivePaneIdsRef.current, paneId]));
      setPanesWithIn((prev) => (prev.has(paneId) ? prev : new Set(prev).add(paneId)));
    },
    [setExtraActive, setExtraActivePanes]
  );

  /** `removeUserDefinedField` (`:306-328`) — §6 W6 against `prp/`. */
  const removeUserDefinedField = useCallback(() => {
    const id = popupForRef.current;
    if (id == null) return;
    /**
     * `:308-310` — the sibling to activate afterwards is looked up as
     * `#proposal-row-container li.active.each-element-row[data-id="<id>"]`, i.e.
     * it exists ONLY when the field being deleted is the currently-selected one.
     * ★ Proposal then ALSO records it as `self.last_clicked` (`:310`), which
     * submittal does not — and which is what makes the following `render()`
     * re-select it.
     */
    const key = proposalItemKey('user', id);
    const isActive =
      activeItemKeyByTypeRef.current.user === key || extraActiveKeysRef.current.has(key);
    const index = fieldsRef.current.findIndex((f) => f.id === id);
    const sibling = isActive
      ? fieldsRef.current[index + 1] ?? fieldsRef.current[index - 1]
      : undefined;
    if (sibling) lastClickedRef.current = String(sibling.id);

    setDeletePopupField(null);
    // Optimistic, exactly like Backbone's `destroy()` with no `{wait:true}`.
    // ★★ On the server this also triggers R4's PRP twin — the collateral delete
    // of every PRP format row whose `object_id` matches, across content types
    // AND across users (`viewset.py:122`). Not guarded here; the client cannot
    // fix it.
    deleteUdf(id);
    patchUdfCache((draft) => {
      const i = draft.findIndex((f) => f.id === id);
      if (i >= 0) draft.splice(i, 1);
    });

    // `$('.li-<id>').remove()` + `$('.proposal_user_defined_field_<id>').remove()`
    // — the row and its pane leave the RENDERED markup immediately.
    const nextFields = fieldsRef.current.filter((f) => f.id !== id);
    fieldsRef.current = nextFields;

    /**
     * `:316-325` — re-index the format collection, dropping every row whose
     * `object_id` matches the deleted field.
     * ★ The filter is `el.object_id != self.popup_for` and NOTHING ELSE: no
     * content-type check, so a format row pointing at a
     * `MasterProposalPredefinedField` that happens to share the id is dropped
     * too. That is the client-side twin of R4, mirrored rather than corrected.
     */
    const previousOrder = new Map(formatRef.current.map((r) => [r.id, r.order_id]));
    const nextFormat: IUserSettingsPrpFormatRow[] = [];
    let order = 0;
    formatRef.current.forEach((row) => {
      if (row.object_id !== id) {
        nextFormat.push({ ...row, order_id: order });
        order += 1;
      }
    });
    formatRef.current = nextFormat;

    /**
     * ★ `collectionPrpFormat.set(new_list)` is handed PLAIN OBJECTS, so Backbone
     * MERGES them into the existing models — and any surviving row whose
     * `order_id` moved fires `'change'`, which on this tab means a full
     * `render()`. (Submittal does the identical `set` and renders nothing,
     * because its format listener is commented out.) So the rail here updates
     * immediately when a delete shifts the ordering, and only 300 ms later via
     * `render_selects` when it does not.
     */
    const orderChanged = nextFormat.some((r) => previousOrder.get(r.id) !== r.order_id);
    if (orderChanged) doRender(nextFields, nextFormat);
    else setSnap((prev) => (prev ? { ...prev, userFields: nextFields } : prev));

    // `sibling?.click()` clicks the `<li>`, which Bootstrap's tab plugin does
    // NOT see (it is bound to the `<a>`), so only `clickToItem` runs.
    if (sibling) clickToItem(userItem(sibling));
    renderSelects();
  }, [clickToItem, deleteUdf, doRender, patchUdfCache, renderSelects]);

  // ═══════════════════════════════════════════════════════════════════════
  // WRITE-SIDE HANDLERS — the format rail (§6 W7 / W8 / W9)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * `updateOrdering` (`:515-534`) — §6 W7's single request, shared by both drag
   * paths. ★ NO RE-FETCH AND NO RE-RENDER: the response echoes the request (the
   * serializer returns `validated_data`), and legacy uses it only to REORDER the
   * collection in memory. Two details are reproduced deliberately:
   *  · `model.order_id = …` sets a stray OWN property, not
   *    `model.attributes.order_id` — so `toJSON()` keeps reporting the OLD
   *    `order_id` values in the NEW array order, AND (crucially on this tab) it
   *    fires no `'change'`, so it does not re-render. The rows below are
   *    reordered without rewriting `order_id`, matching exactly.
   *  · `collection.set(new_list)` runs with Backbone's default `{remove:true}`,
   *    so anything absent from `data.settings` LEAVES the collection; and
   *    Backbone dedupes by id, which matters because `changeOrder` can
   *    legitimately emit the same id twice.
   * `updateOrdering`'s `fetch` parameter is never passed by either call site, so
   * its `fetch_proposal_format()` branch is dead and `this.scrollTop` is
   * captured only for it.
   */
  const updateOrdering = useCallback(
    async (settings: IUserSettingsPrpOrderEntry[]) => {
      const data = await dragFormat({ settings }).unwrap();
      if (!data?.settings) return;
      const byId = new Map(formatRef.current.map((row) => [row.id, row]));
      const next: IUserSettingsPrpFormatRow[] = [];
      const seen = new Set<number>();
      data.settings.forEach((item) => {
        const row = byId.get(item.id);
        // ⚠ Legacy would THROW here on a miss. Unreachable in practice — the
        // server echoes back the ids we just sent — and a thrown TypeError would
        // only abandon the reorder half-done, so the miss is skipped.
        if (!row || seen.has(row.id)) return;
        seen.add(row.id);
        next.push(row);
      });
      formatRef.current = next;
    },
    [dragFormat]
  );

  /**
   * ★★ `changeOrder` (`:535-551`) — TRANSCRIBED LINE FOR LINE, INCLUDING THE
   * SKIPPED TAIL ENTRY. W8's second request, and NOT the same builder as W7's.
   *
   * By the time it runs, `addStructureField` has appended the newly created row,
   * so `models` is N+1 long and `last_index` points AT THE NEW ROW. The loop
   * splices the new id in at the drop `index` and SKIPS `last_index`, i.e. the
   * new row's own tail entry:
   *  · mid-list drop (`index < last_index`) — every model appears exactly once.
   *  · drop at the very END (`index === last_index`) — the `idx === index`
   *    branch fires for the new row ITSELF, emitting `{id:new, order_id:N}` AND
   *    `{id:new, order_id:N+1}`: the same id twice, and the surviving `order_id`
   *    skips a number. Discovery §15.3 flagged the two extremes as unverified;
   *    this is what the code does at them, and it is what ships.
   *
   * Entries are `{ id, order_id }` — id FIRST and a NUMBER, because they come off
   * `collection.toJSON()`. W7's come off the DOM and are the other way round.
   */
  const changeOrder = useCallback(
    async (index: number, id: number) => {
      const models = formatRef.current;
      const lastIndex = models.length - 1;
      const arr: IUserSettingsPrpOrderEntry[] = [];
      models.forEach((element, idx) => {
        if (idx < index) arr.push({ id: element.id, order_id: idx });
        else if (idx === index)
          arr.push({ id, order_id: idx }, { id: element.id, order_id: idx + 1 });
        else if (idx !== lastIndex) arr.push({ id: element.id, order_id: idx + 1 });
      });
      await updateOrdering(arr);
    },
    [updateOrdering]
  );

  /**
   * `addStructureField` (`:487-493`) — §6 W8's first request. `collection.add`
   * APPENDS, which is the premise `changeOrder`'s `last_index` rests on.
   *
   * ★ Also bound directly to `#add_field_to_structure` (`:45`), which submittal
   * does not bind at all — Q18. The button is `hide`den inside a `hidden` header
   * row, so the only reachable caller is `createNewField`; the binding is
   * reproduced regardless. Note it renders NOTHING on its own: `collection.add`
   * fires `'add'`, not `'change'`, so no `render()` follows.
   */
  const addStructureField = useCallback(async () => {
    const field = await createFormat().unwrap();
    formatRef.current = [...formatRef.current, field];
    return field;
  }, [createFormat]);

  const onAddStructureField = useCallback(() => {
    void addStructureField().catch(() => {
      /* legacy `await`s a rejected save and lets it bubble; no UI either way */
    });
  }, [addStructureField]);

  /**
   * `fetch_proposal_format` (`:494-503`), reached through
   * `Dispatcher 'proposal:format:changed'`.
   *
   * ★ THE RE-FETCH BRANCH IS UNREACHABLE, so it is not ported. The event has
   * exactly one publisher — `ModelPRPStructure.onchange`
   * (`proposal/model/prp.format.js:29-42`), which fires it in the `.then` of the
   * save that a plain `set()` triggers. The only such `set()` in the view is
   * `createNewField`'s (`:572`), which assigns `last_action='create_new_field'`
   * on the line before.
   *
   * ⚠ ONE DIFFERENCE FROM SUBMITTAL, AND IT CANCELS OUT: submittal's early
   * return calls `self.render()` (`submittal.js:695`); proposal's just returns
   * (`:498`). Proposal still re-renders at that moment, because the save's
   * SUCCESS `set(serverAttrs)` fires the format collection's `'change'` →
   * `render`. Same single render, same place in the sequence, different wire.
   */
  const fetchProposalFormat = useCallback(() => {
    lastActionRef.current = '';
    doRender(fieldsRef.current, formatRef.current);
  }, [doRender]);

  /**
   * `createNewField` (`:560-605`) — §6 W8, the three-request dance, in order:
   *   1. `POST …/prp/format/ {}`                    (`addStructureField`)
   *   2. `PATCH …/prp/format/drag/ {settings:[…]}`  (`changeOrder`)
   *   3. `PATCH …/prp/format/<id>` with the type payload
   *
   * ★ THE THIRD REQUEST IS NOT AWAITED BEFORE THE DOM CHANGES. Legacy's
   * `field.set(...)` fires `onchange` → `save()` and carries straight on; only
   * the `.then` of that save reaches the dispatcher. So the rail shows a
   * placeholder row for one round-trip, and if the PATCH fails it shows it
   * forever (verified: dropping a CMS doc whose key is longer than
   * `cms_field_name`'s 15 chars really does 500, and the row stays).
   *
   * ★★★ Q12 DOES NOT REACH THE LIVE DOM — MEASURED, NOT ASSUMED.
   *
   * `proposal.js:21` really does compile `templateSelectItem` from
   * `submittal/tmpl/submittal.format.item.tmpl`, and `createNewField` really
   * does build a submittal-flavoured row out of it. But `field.set(...)` on the
   * line above fires the format collection's `'change'` → **`render()`, which
   * runs SYNCHRONOUSLY and replaces `$el.html()`**. The `$nextRow` / `$prevRow`
   * anchors were captured before the two `await`s, so by the time
   * `$nextRow.before(newRowHtml)` runs they are DETACHED nodes and the injected
   * row lands in an orphaned tree. The only branch that would reach the live
   * wrapper is `$wrapper.prepend(...)`, and it is unreachable: it needs
   * `ui.item` to have neither a previous nor a next `.select-row[data-index]`,
   * i.e. an empty rail.
   *
   * ⚠ WAVE-4 VERIFY, WITH THE THIRD PATCH STALLED FOR 9 s, LEGACY SHOWED:
   *     id `row-<real id>` · `col-xs-12 item-for-drag select-row
   *     select-row-click ui-sortable-handle` · `<span class="prօposal_format_field">`
   *     · label "" (the POST response's `name` is null) · data-model "" ·
   *     data-parent `.left-column-proposal-scrollable` · trash data-id `<real id>`
   *     · AND IT DRAGS: moving it emitted a full `/prp/format/drag/` PATCH
   *       carrying its real id.
   * The earlier port injected submittal's row instead — `row-`, no
   * `item-for-drag`, `submittal_format_field`, the LIBRARY label as its text,
   * `draggable=false`, and a drag that emitted nothing. Every one of those is
   * user-visible for a round-trip, and permanent on the 500. So what ships is
   * legacy's intermediate render, and the injected row is not built at all.
   *
   * ★ Duplicates are allowed: dropping the same library item twice legitimately
   * produces two rows with the same `object_id`.
   */
  const createNewField = useCallback(
    async (payload: ProposalDragPayload, newIndex: number) => {
      const targetType = payload.type;
      const targetName = payload.name;
      const targetId = payload.id;

      let field: IUserSettingsPrpFormatRow;
      try {
        field = await addStructureField();
        await changeOrder(newIndex, field.id);
      } catch {
        // Either request failing leaves legacy's `await` rejected and the rest of
        // `createNewField` unrun: no third PATCH, no render. (A failed
        // step 2 leaves the blank placeholder row step 1 created, which is
        // exactly what legacy leaves behind.)
        return;
      }
      lastActionRef.current = 'create_new_field';

      // `field.set({object_id, type, cms_field_name})` → `onchange` →
      // `save(self.changed, {patch:true})`. Built in the legacy key order so the
      // JSON matches; the undefined members drop out.
      const patched = patchFormat({
        id: field.id,
        body: { object_id: targetId, type: targetType, cms_field_name: targetName },
      }).unwrap();

      // ── the synchronous half: `set()` → collection `'change'` → render() ──
      // Only the two attributes the TEMPLATE reads move onto the local model.
      // `type` is a write-only discriminator and `model` / `name` stay as the
      // POST left them (both null), which is why the row paints BLANK until the
      // PATCH resolves. `object_id` is `Number`ed because the row type says so;
      // `data-target` renders the same string either way, and `undefined`
      // (a CMS drop, whose anchor has no `data-id`) becomes `null` → `""`.
      formatRef.current = formatRef.current.map((row) =>
        row.id === field.id
          ? {
              ...row,
              object_id: targetId === undefined ? null : Number(targetId),
              cms_field_name: targetName ?? null,
            }
          : row
      );
      // `$wrapper.find(".empty-row").appendTo($wrapper)` and the `data-index`
      // renumbering that follow it are both no-ops against a fresh render: the
      // sentinel is emitted after the rows by construction and `data-index` is
      // the map position.
      doRender(fieldsRef.current, formatRef.current);

      // ── the async tail: the save's `.then` → the dispatcher → render() ────
      let updated: IUserSettingsPrpFormatRow;
      try {
        updated = await patched;
      } catch {
        // `.then` never runs → no dispatcher → no SECOND render, and the row
        // keeps the blank, locally-`set` shape above. ★ This is the branch R7
        // would land in for a `user_defined` drop with no `object_id`, R8 for a
        // missing `type`, and the one a >15-char `cms_field_name` lands in.
        return;
      }
      formatRef.current = formatRef.current.map((row) =>
        row.id === field.id ? { ...row, ...updated } : row
      );
      fetchProposalFormat();
    },
    [addStructureField, changeOrder, doRender, fetchProposalFormat, patchFormat]
  );

  /**
   * The DOM-order payload builder shared by W7's two triggers — `stop`'s
   * non-dropping branch (`:625-634`) verbatim:
   *   Array.from($$('#proposal-format-list-wrapper-row .select-row[data-index]'))
   *     .map((row, order_id) => ({ order_id, id: row.id.replace('row-','') }))
   * ★ It reads the RENDERED rail, not the collection — hence `rail.rows` — and
   * `order_id` comes FIRST with `id` a STRING sliced off the DOM id.
   */
  const domOrderPayload = useCallback(
    (): IUserSettingsPrpOrderEntry[] =>
      rail.rows.map((row, order_id) => ({ order_id, id: String(row.id) })),
    [rail.rows]
  );

  /** Live shuffle during an internal drag. Only the rendered rail moves; the
   * collection follows from the RESPONSE, in `updateOrdering`. */
  const onRowsMove = useCallback((from: number, to: number) => {
    setRail((prev) => {
      if (from === to || from < 0 || to < 0 || from >= prev.rows.length || to >= prev.rows.length) {
        return prev;
      }
      const rows = [...prev.rows];
      const [moved] = rows.splice(from, 1);
      rows.splice(to, 0, moved);
      return { ...prev, rows };
    });
  }, []);

  /** §6 W7 — the reorder PATCH. ★ Fired on EVERY drag end, movement or not:
   * jQuery-UI's `stop` has no "did anything change" test and neither does this. */
  const onReorderCommit = useCallback(() => {
    void updateOrdering(domOrderPayload()).catch(() => {
      // `fn.uni_tools.patch` throws and `stop` does not catch — an uncaught
      // rejection, with the rail left showing the dropped order. Same outcome
      // here, minus the console noise.
    });
  }, [domOrderPayload, updateOrdering]);

  /** ★★ Q22 — a plain click on a rail row emits a full no-op `/drag/` PATCH,
   * because jQuery-UI fires `stop` for a zero-distance drag. `ProposalFormatRail`
   * decides WHEN. */
  const onRowPlainClick = onReorderCommit;

  /** The library-drag half of `stop` (`:619-624`) → `createNewField`. */
  const onCreateAtIndex = useCallback(
    (index: number) => {
      const payload = dragPayloadRef.current;
      if (!payload) return;
      void createNewField(payload, index);
    },
    [createNewField]
  );

  /**
   * `init_draggable($(".draggable-item"))` (`:638-654`): the library half of the
   * drag — armed by every anchor's `mousedown`, dropping only on
   * `#proposal-format-list-wrapper-row`.
   */
  const libraryDrag = useLibraryDrag<ProposalDragPayload>({
    railRef,
    scrollerRef: railWrapperRef,
    dragPayloadRef,
    onDrop: onCreateAtIndex,
  });

  /**
   * `render_delete_popup_proposal` (`:458-470`) — §6 W9's popup, with
   * `type: "Proposal"` (`:466`) where submittal passes `"Submittal"` into the
   * SAME template. ★ Q4: called TWICE per click, appending two identical popups.
   */
  const onFormatDeleteClick = useCallback((domId: string) => {
    deletePopupForRef.current = domId;
    const row = formatRef.current.find((r) => String(r.id) === domId);
    // ⚠ Legacy does `collection.get(id).toJSON()` unguarded, so a miss throws
    // and no popup appears. Every rendered row now comes from the collection, so
    // a miss is unreachable — the guard stays as the same observable outcome.
    if (!row) return;
    setDeleteFormatPopups((prev) => [...prev, row]);
  }, []);

  /** `removePRPStructureField` (`:504-514`) — §6 W9. */
  const removePRPStructureField = useCallback(() => {
    const domId = deletePopupForRef.current;
    if (domId == null) return;
    // `$('#row-<id>').remove()` — the row leaves the RENDERED rail BEFORE the
    // request goes out, and `data-index` renumbers itself from the map position.
    setRail((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => String(r.id) !== domId),
    }));
    // `remove_confirm_popup(undefined, '.delete-field')` removes the jQuery SET
    // — i.e. BOTH of Q4's popups.
    setDeleteFormatPopups([]);
    const row = formatRef.current.find((r) => String(r.id) === domId);
    if (!row) return;
    // Backbone `destroy()`: optimistic, never reverted, and the model leaves the
    // collection immediately (a `'destroy'`/`'remove'`, NOT a `'change'`, so no
    // render follows).
    // ★ R3 — unlike submittal, the SERVER re-indexes `order_id` here
    // (`UserSettingsPRPStructure.delete`, `models.py:74-78`). The CLIENT still
    // does not, and must not: the next ordering PATCH is what the client
    // believes.
    deleteFormat(row.id);
    formatRef.current = formatRef.current.filter((r) => r.id !== row.id);
  }, [deleteFormat]);

  // `#proposal_format` stays EMPTY until all four fetches resolve — the view's
  // first `render()` lives inside `Promise.all(...).then(...)`.
  if (!snap) return null;

  /** The exclusive `.active` — ONE PER `<ul>`, because Bootstrap's `activate`
   * only sweeps the list it was handed — UNION the additive one
   * (`active_last_change` / `activateTab`). */
  const activeItemKeys: ReadonlySet<string> = new Set(
    [
      activeItemKeyByType.user,
      activeItemKeyByType.cms,
      activeItemKeyByType.application,
      ...extraActiveKeys,
    ].filter((k): k is string => k !== null)
  );
  /** The Bootstrap-exclusive pane UNION the additively-activated ones. */
  const activePaneIds: ReadonlySet<string> = new Set(
    [activePaneId, ...extraActivePaneIds].filter((k): k is string => k !== null)
  );

  return (
    <>
      {/* ★ Q3 — `proposal.tmpl:1`'s root really is `<div id="submittal">`, a
          copy-paste leftover. Three elements share that id on the real page (the
          nav anchor, the submittal pane root and this one) and
          `document.getElementById('submittal')` resolves to the nav anchor.
          Load-bearing: `styles.css` selects `#submittal .…` and CSS id selectors
          match every one of them — which is exactly why the PROPOSAL view's own
          `$('#proposal …')` lookups all miss (see the banner above). Kept. */}
      <div id="submittal" className="col-xs-12 full-height">
        <div className="full-height">
          <div className="col-xs-12 full-height">
            <div className="row full-height">
              <ProposalFormatRail
                rows={rail.rows}
                variant={rail.variant}
                activeRowId={activeRowId}
                tooltipRowIds={tooltipRowIds}
                onRowClick={onRowClick}
                onWrapperScroll={onWrapperScroll}
                wrapperRef={railWrapperRef}
                labelRefs={labelRefs}
                railRef={railRef}
                libraryPlaceholderIndex={libraryDrag.placeholderIndex}
                onRowsMove={onRowsMove}
                onReorderCommit={onReorderCommit}
                onRowPlainClick={onRowPlainClick}
                onDeleteClick={onFormatDeleteClick}
                onAddStructureField={onAddStructureField}
              />
              <div className="col-xs-9 full-height">
                <div className="row full-height right-column user_settings-content-user-fields">
                  <ProposalFieldLibrary
                    userFields={snap.userFields}
                    cmsDocuments={cmsDocuments}
                    appFields={appFields}
                    activeType={activeType}
                    counts={counts}
                    searchValue={searchValue}
                    searchHidden={searchHidden}
                    onSearchChange={setSearchValue}
                    onChipClick={changeActiveDefinedsField}
                    onAddClick={() => void addUserDefinedField()}
                    onItemClick={(_type, item) => selectLibraryItem(item)}
                    onRemoveClick={onRemoveClick}
                    onDeleteButtonActivate={activateTab}
                    activeItemKeys={activeItemKeys}
                    tooltipItemKeys={tooltipItemKeys}
                    itemRefs={itemRefs}
                    anchorRefs={anchorRefs}
                    userScrollRef={userScrollRef}
                    onItemMouseDown={libraryDrag.onAnchorMouseDown}
                  />
                  <hr />
                  <ProposalFieldDetail
                    userFields={snap.userFields}
                    appFields={appFields}
                    cmsDocuments={cmsDocuments}
                    terms={terms}
                    gen={snap.gen}
                    activePaneIds={activePaneIds}
                    panesWithIn={panesWithIn}
                    onTitleBlur={onTitleBlur}
                    onContentBlur={onContentBlur}
                    onFieldInput={changeUserDefinedField}
                    onCmsTitleCheck={onCmsTitleCheck}
                    invalidCmsTitles={invalidCmsTitles}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Both popups are `self.$el.append(...)`ed into `#proposal_format` — the
          PANE, not `<body>` and not the `#submittal` root div. Rendered as its
          siblings for that reason. */}
      {deletePopupField && (
        <DeleteUdfPopup
          field={deletePopupField}
          onConfirm={removeUserDefinedField}
          onCancel={() => setDeletePopupField(null)}
        />
      )}
      {/* ★★ Q4 — TWO of these per click, stacked and identical. Cancel clears
          both, because `remove_confirm_popup` removes the jQuery SET. The key is
          the array position for exactly that reason. */}
      {deleteFormatPopups.map((format, i) => (
        <DeleteFormatFieldPopup
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          format={format}
          /* ★ `render_delete_popup_proposal` passes `type: "Proposal"` into the
             same `tmpl/delete.format.field.tmpl` submittal feeds `"Submittal"` —
             "Delete Proposal Field?" / "…generated Proposals." */
          type="Proposal"
          onConfirm={removePRPStructureField}
          onCancel={() => setDeleteFormatPopups([])}
        />
      ))}
    </>
  );
};

export default ProposalTab;
